import { useState, useRef, useEffect } from 'react';
import { Send, Square, Paperclip } from 'lucide-react';
import { useChat } from '../context/ChatContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

export default function Composer() {
  const { sendMessage, isStreaming, stopGeneration, currentThread } = useChat();
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isStreaming || !currentThread) return;
    
    const message = input;
    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const suggestedPrompts = [
    'Show me sales KPIs for this month',
    'Run RFM segmentation analysis',
    'What are the top performing stores?',
    'Show loyalty program metrics',
  ];

  return (
    <div className="border-t border-border/50 bg-background/80 backdrop-blur-lg" data-testid="composer">
      {/* Suggested prompts when no thread or empty */}
      {currentThread && (
        <div className="px-4 py-2 flex gap-2 overflow-x-auto">
          {suggestedPrompts.map((prompt, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              className="whitespace-nowrap text-xs"
              onClick={() => setInput(prompt)}
              data-testid={`suggested-prompt-${idx}`}
            >
              {prompt}
            </Button>
          ))}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="p-4">
        <div className="relative flex items-end gap-2 max-w-3xl mx-auto">
          {/* Attachment button (placeholder for future) */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 h-10 w-10"
            disabled
            title="Attachments coming soon"
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          
          {/* Input */}
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={currentThread ? "Ask about your loyalty data..." : "Select or create a chat to start"}
              disabled={!currentThread || isStreaming}
              className="min-h-[44px] max-h-[200px] pr-12 resize-none rounded-xl"
              rows={1}
              data-testid="message-input"
            />
          </div>
          
          {/* Send/Stop button */}
          {isStreaming ? (
            <Button
              type="button"
              size="icon"
              variant="destructive"
              className="shrink-0 h-10 w-10 rounded-xl"
              onClick={stopGeneration}
              data-testid="stop-btn"
            >
              <Square className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon"
              className="shrink-0 h-10 w-10 rounded-xl"
              disabled={!input.trim() || !currentThread}
              data-testid="send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        <p className="text-center text-xs text-muted-foreground mt-2">
          Fundle GPT can make mistakes. Verify important analytics.
        </p>
      </form>
    </div>
  );
}
